/**
 * Dialoghi archive — format filters, topics, load more, scroll lock.
 *
 * @package HelloElementorChild
 */

import { initCamstReveal, refreshCamstReveal } from './camst-reveal.js';

var dialoghiInstances = new WeakMap();
var dialoghiRequestIds = new WeakMap();

function getState(root) {
  var initialShown = parseInt(root.getAttribute('data-initial-shown') || '0', 10) || 0;

  return (
    dialoghiInstances.get(root) || {
      format: root.getAttribute('data-initial-format') || 'scintille',
      topic: '',
      offset: initialShown,
      shown: initialShown,
    }
  );
}

function setState(root, state) {
  dialoghiInstances.set(root, state);
}

function nextDialoghiRequestId(root) {
  var next = (dialoghiRequestIds.get(root) || 0) + 1;
  dialoghiRequestIds.set(root, next);
  return next;
}

function isLatestDialoghiRequest(root, requestId) {
  return dialoghiRequestIds.get(root) === requestId;
}

function setLoading(root, isLoading) {
  var itemsWrap = root.querySelector('[data-camst-dialoghi-items]');
  var loader = root.querySelector('[data-camst-dialoghi-loader]');

  root.classList.toggle('is-loading', isLoading);

  if (itemsWrap) {
    itemsWrap.classList.toggle('is-loading', isLoading);
  }

  if (loader) {
    loader.hidden = !isLoading;
  }
}

function bindFeaturedCarousel(root) {
  var track = root.querySelector('[data-camst-featured-track]');

  if (!track) {
    return;
  }

  var prev = root.querySelector('[data-camst-featured-prev]');
  var next = root.querySelector('[data-camst-featured-next]');

  function scrollBySlide(direction) {
    track.scrollBy({
      left: direction * track.clientWidth,
      behavior: 'smooth',
    });
  }

  if (prev) {
    prev.addEventListener('click', function () {
      scrollBySlide(-1);
    });
  }

  if (next) {
    next.addEventListener('click', function () {
      scrollBySlide(1);
    });
  }
}

function updateFormatUi(root, format, description) {
  root.querySelectorAll('[data-camst-dialoghi-tab]').forEach(function (button) {
    var isActive = button.getAttribute('data-camst-dialoghi-format') === format;
    button.classList.toggle('is-active', isActive);
  });

  root.querySelectorAll('.camst-dialoghi-archive__format-card[data-camst-dialoghi-format]').forEach(function (button) {
    var isActive = button.getAttribute('data-camst-dialoghi-format') === format;
    button.classList.toggle('is-active', isActive);
  });

  root.querySelectorAll('[data-camst-dialoghi-format-description]').forEach(function (node) {
    node.textContent = description || '';
  });

  root.querySelectorAll('[data-camst-dialoghi-format-description-mobile]').forEach(function (node) {
    node.textContent = description || '';
  });
}

function renderTopics(root, topics, activeTopic) {
  var containers = [
    root.querySelector('[data-camst-dialoghi-topics]'),
    root.querySelector('[data-camst-dialoghi-topics-mobile]'),
  ].filter(Boolean);
  var seen = Object.create(null);

  containers.forEach(function (container) {
    container.innerHTML = '';

    if (!topics.length) {
      container.hidden = true;
      return;
    }

    container.hidden = false;
    seen = Object.create(null);

    topics.forEach(function (topic) {
      var slug = topic.slug || '';
      var label = (topic.label || '').trim();
      var labelKey = label.toLocaleLowerCase('it-IT');

      if (!slug || !label) {
        return;
      }

      if (seen[slug] || (labelKey && seen[labelKey])) {
        return;
      }

      seen[slug] = true;

      if (labelKey) {
        seen[labelKey] = true;
      }

      var button = document.createElement('button');
      var labelNode = document.createElement('span');
      button.type = 'button';
      button.className =
        'camst-dialoghi-archive__topic' + (slug === activeTopic ? ' is-active' : '');
      button.setAttribute('data-camst-dialoghi-topic', slug);
      labelNode.className = 'camst-dialoghi-archive__topic-label';
      labelNode.textContent = label.toLocaleUpperCase('it-IT');
      button.appendChild(labelNode);
      container.appendChild(button);
    });
  });
}

function ensureLoadMoreButton(footer) {
  var loadMore = footer.querySelector('[data-camst-dialoghi-load-more]');

  if (loadMore) {
    return loadMore;
  }

  var showLess = footer.querySelector('[data-camst-dialoghi-show-less]');
  loadMore = document.createElement('button');
  loadMore.type = 'button';
  loadMore.className = 'camst-dialoghi-archive__load-more';
  loadMore.setAttribute('data-camst-dialoghi-load-more', '');
  loadMore.innerHTML =
    '<span>Carica altri</span><i class="fas fa-plus" aria-hidden="true"></i>';

  if (showLess) {
    footer.insertBefore(loadMore, showLess);
  } else {
    footer.appendChild(loadMore);
  }

  return loadMore;
}

function updateFooter(root, payload, perPage) {
  var footer = root.querySelector('[data-camst-dialoghi-footer]');
  var counter = root.querySelector('[data-camst-dialoghi-counter]');
  var showLess = root.querySelector('[data-camst-dialoghi-show-less]');

  if (!footer || !counter) {
    return;
  }

  var loadMore = ensureLoadMoreButton(footer);

  counter.textContent =
    'Mostrati ' + (payload.shown || 0) + ' di ' + (payload.total || 0) + ' contenuti';

  loadMore.hidden = (payload.shown || 0) >= (payload.total || 0);

  if (showLess) {
    showLess.hidden = (payload.total || 0) <= perPage || (payload.shown || 0) <= perPage;
  }
}

function collectShownDialoghiIds(root) {
  var itemsWrap = root.querySelector('[data-camst-dialoghi-items]');

  if (!itemsWrap) {
    return [];
  }

  return Array.prototype.slice
    .call(itemsWrap.querySelectorAll('[data-camst-dialoghi-id]'))
    .map(function (node) {
      return parseInt(node.getAttribute('data-camst-dialoghi-id') || '0', 10) || 0;
    })
    .filter(function (id) {
      return id > 0;
    });
}

function fetchArchive(root, nextState, options) {
  var opts = options || {};
  var requestId = nextDialoghiRequestId(root);
  var restUrl = root.getAttribute('data-rest-url');
  var perPage = parseInt(root.getAttribute('data-per-page') || '4', 10) || 4;
  var itemsWrap = root.querySelector('[data-camst-dialoghi-items]');
  var url = new URL(restUrl, window.location.origin);
  var excludeIds = opts.replace ? [] : collectShownDialoghiIds(root);

  url.searchParams.set('format', nextState.format);
  url.searchParams.set('topic', nextState.topic || '');
  url.searchParams.set('offset', String(excludeIds.length ? 0 : nextState.offset || 0));
  url.searchParams.set('per_page', String(perPage));

  if (excludeIds.length) {
    url.searchParams.set('exclude', excludeIds.join(','));
  }

  setLoading(root, true);

  return fetch(url.toString(), {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  })
    .then(function (response) {
      if (!response.ok) {
        throw new Error('Dialoghi request failed');
      }

      return response.json();
    })
    .then(function (payload) {
      if (!isLatestDialoghiRequest(root, requestId)) {
        return;
      }

      if (!itemsWrap) {
        return;
      }

      if (payload.code) {
        throw new Error(payload.message || 'Dialoghi request failed');
      }

      if (opts.replace) {
        var loader = itemsWrap.querySelector('[data-camst-dialoghi-loader]');
        itemsWrap.innerHTML = payload.html || '';

        if (loader) {
          itemsWrap.prepend(loader);
        }
      } else {
        itemsWrap.insertAdjacentHTML('beforeend', payload.html || '');
      }

      itemsWrap.dataset.activeFormat = nextState.format;
      itemsWrap.classList.toggle(
        'camst-dialoghi-archive__items--podcast',
        nextState.format === 'podcast'
      );
      itemsWrap.classList.toggle(
        'camst-dialoghi-archive__items--list',
        nextState.format !== 'podcast'
      );

      updateFormatUi(root, nextState.format, payload.description || '');
      renderTopics(root, payload.topics || [], payload.topic || nextState.topic || '');
      updateFooter(root, payload, perPage);
      setState(root, {
        format: nextState.format,
        topic: payload.topic || nextState.topic || '',
        offset: payload.shown || 0,
        shown: payload.shown || 0,
      });

      if (opts.resetScroll) {
        var main =
          root.querySelector('[data-camst-dialoghi-main-scroll]') ||
          root.querySelector('.camst-dialoghi-archive__main');

        if (main) {
          main.scrollTop = 0;
        }
      }

      refreshCamstReveal(root);
    })
    .catch(function () {
      if (!isLatestDialoghiRequest(root, requestId)) {
        return;
      }
    })
    .finally(function () {
      if (isLatestDialoghiRequest(root, requestId)) {
        setLoading(root, false);
      }
    });
}

function bindScrollLock(root) {
  var grid = root.querySelector('[data-camst-dialoghi-grid]');
  var main = root.querySelector('.camst-dialoghi-archive__main');

  if (!grid || !main) {
    return;
  }

  main.setAttribute('data-camst-dialoghi-main-scroll', '');

  function onWheel(event) {
    if (window.innerWidth < 1024) {
      return;
    }

    var rect = grid.getBoundingClientRect();
    var topOffset = 85;
    var isScrollingDown = event.deltaY > 0;
    var isAtBottom = main.scrollTop + main.clientHeight >= main.scrollHeight - 5;
    var isAtTop = main.scrollTop <= 5;
    var shouldLock = false;

    if (isScrollingDown) {
      if (rect.top <= topOffset + 15 && !isAtBottom) {
        shouldLock = true;
      }
    } else if (rect.top >= topOffset - 15 && !isAtTop) {
      shouldLock = true;
    }

    if (shouldLock) {
      event.preventDefault();
      var alignedScrollY = window.scrollY + rect.top - topOffset;
      window.scrollTo({ top: alignedScrollY, behavior: 'auto' });
      main.scrollTop += event.deltaY;
    }
  }

  window.addEventListener('wheel', onWheel, { passive: false });
}

function bindInteractions(root) {
  var perPage = parseInt(root.getAttribute('data-per-page') || '4', 10) || 4;

  root.addEventListener('click', function (event) {
    var formatButton = event.target.closest('[data-camst-dialoghi-format]');
    var topicButton = event.target.closest('[data-camst-dialoghi-topic]');
    var loadMore = event.target.closest('[data-camst-dialoghi-load-more]');
    var showLess = event.target.closest('[data-camst-dialoghi-show-less]');

    if (formatButton && root.contains(formatButton)) {
      var format = formatButton.getAttribute('data-camst-dialoghi-format') || 'scintille';
      var nextState = { format: format, topic: '', offset: 0 };
      fetchArchive(root, nextState, { replace: true, resetScroll: true });
      return;
    }

    if (topicButton && root.contains(topicButton)) {
      var current = getState(root);
      var slug = topicButton.getAttribute('data-camst-dialoghi-topic') || '';
      var nextTopic = current.topic === slug ? '' : slug;
      var nextTopicState = {
        format: current.format,
        topic: nextTopic,
        offset: 0,
      };
      fetchArchive(root, nextTopicState, { replace: true, resetScroll: true });
      return;
    }

    if (loadMore && root.contains(loadMore)) {
      event.preventDefault();
      var currentLoad = getState(root);
      var nextOffset = currentLoad.shown ?? currentLoad.offset ?? 0;

      fetchArchive(
        root,
        {
          format: currentLoad.format,
          topic: currentLoad.topic,
          offset: nextOffset,
        },
        { replace: false }
      );
      return;
    }

    if (showLess && root.contains(showLess)) {
      event.preventDefault();
      var currentLess = getState(root);

      fetchArchive(
        root,
        {
          format: currentLess.format,
          topic: currentLess.topic,
          offset: 0,
        },
        { replace: true, resetScroll: true }
      );
    }
  });
}

export function initCamstDialoghiArchive(scope) {
  var roots = [];

  if (!scope) {
    roots = Array.prototype.slice.call(
      document.querySelectorAll('[data-camst-dialoghi-archive]')
    );
  } else if (scope.matches && scope.matches('[data-camst-dialoghi-archive]')) {
    roots = [scope];
  } else {
    roots = Array.prototype.slice.call(
      scope.querySelectorAll('[data-camst-dialoghi-archive]')
    );
  }

  roots.forEach(function (root) {
    if (root.dataset.camstDialoghiArchiveInit === '1') {
      return;
    }

    root.dataset.camstDialoghiArchiveInit = '1';

    var perPage = parseInt(root.getAttribute('data-per-page') || '4', 10) || 4;
    var initialState = getState(root);

    setState(root, initialState);
    updateFooter(
      root,
      {
        shown: initialState.shown,
        total: parseInt(root.getAttribute('data-initial-total') || '0', 10) || 0,
      },
      perPage
    );
    bindFeaturedCarousel(root);
    bindInteractions(root);
    bindScrollLock(root);
    initCamstReveal(root);
  });
}

export function teardownCamstDialoghiArchive(scope) {
  var roots = scope
    ? scope.matches && scope.matches('[data-camst-dialoghi-archive]')
      ? [scope]
      : Array.prototype.slice.call(scope.querySelectorAll('[data-camst-dialoghi-archive]'))
    : [];

  roots.forEach(function (root) {
    delete root.dataset.camstDialoghiArchiveInit;
  });
}
